/**
 * @file 見取り図 — 入園前から年長までを 1 枚の表で見る面 — の計算 (純関数)。
 *
 * @what: 確認の月から時間軸上の位置を出す、ステップの確認と場の点検を並べる、お子さんの
 *   いまのステップで「次に何をするか」の候補 (取り組み中 / 教材が決まっていない単元 /
 *   教材を使い終えた単元) を引く。
 * @why: 「どの段階で何をどこまで」はモデルの中に、ステップの終わりの状態 (exit)・確認と
 *   場の点検 (calibration_points)・単元ごとの水準 (expected) として散らばっている。
 *   表に並べるための計算をここに閉じ、描画側は数えない (代表 2026-08-23)。
 * @failure: 例外を投げない。月が無い確認は時間軸の印にならない (帯で出す)。
 */

import type { StudyMaterialStatus, StudyUnitStatus } from "../api/plans";
import type { AttainmentModel, CalibrationPoint, Unit } from "./types";
import { unitsInCell } from "./parse";

/**
 * 学年の中の月 → 学年の始まり (4 月) を 0、終わり (翌 3 月) を 11/12 とする位置。
 * 時間軸の列の中で印を置く横位置に使う。
 */
export function schoolYearPosition(month: number): number {
  const m = Math.min(12, Math.max(1, Math.round(month)));
  return ((m + 8) % 12) / 12;
}

/** 時期が月で決まる確認か (場の点検は月を持たず帯で出す)。 */
export function isTimedCheck(cp: CalibrationPoint): boolean {
  return cp.month !== undefined;
}

/** ステップの確認と場の点検を、月の順 (月の無いものは後) に並べて返す。 */
export function checkpointsForStage(model: AttainmentModel, stageId: string): CalibrationPoint[] {
  return model.calibrationPoints
    .filter((c) => c.stage === stageId)
    .slice()
    .sort((a, b) => (a.month ?? 99) - (b.month ?? 99));
}

/** ステップの単元を領域の順で全部返す (触れる水準も含む)。 */
export function unitsInStage(model: AttainmentModel, stageId: string): Unit[] {
  return model.domains.flatMap((d) => unitsInCell(model, stageId, d.id));
}

export interface NextSteps {
  /** 取り組み中の単元。 */
  inProgress: Unit[];
  /** まだ始めておらず、教材も決まっていない単元 (触れる水準は教材を使わないので除く)。 */
  noMaterial: Unit[];
  /** 紐づいた教材がすべて使い終わりで、まだ「できた」になっていない単元 (次の 1 冊を考える)。 */
  materialFinished: Unit[];
}

/**
 * いまのステップで次に何をするかの候補。
 *
 * @why: 教材を数冊使い終えたあとに「次は何か」で迷わないよう、教材と単元の状態から
 *   機械的に引けるものだけを出す (代表 2026-08-23)。
 */
export function nextSteps(
  model: AttainmentModel,
  stageId: string,
  unitStates: Readonly<Record<string, { status: StudyUnitStatus }>>,
  materialsByUnit: Readonly<
    Record<string, readonly { status: StudyMaterialStatus; stage_id?: string }[]>
  >,
): NextSteps {
  const out: NextSteps = { inProgress: [], noMaterial: [], materialFinished: [] };
  for (const u of unitsInStage(model, stageId)) {
    const status = unitStates[u.id]?.status ?? "not_started";
    // ステップを決めていない教材はどのステップでも数える。決めてあれば、そのステップだけ
    const materials = (materialsByUnit[u.id] ?? []).filter(
      (m) => !m.stage_id || m.stage_id === stageId,
    );
    if (status === "reached") continue;
    if (status === "in_progress") out.inProgress.push(u);
    if (u.expected[stageId] === "experience") continue;
    if (materials.length === 0) {
      if (status === "not_started") out.noMaterial.push(u);
    } else if (materials.every((m) => m.status === "finished")) {
      out.materialFinished.push(u);
    }
  }
  return out;
}

/**
 * 教材を使う時期 (stageId) に対して、単元 u がその時期以降で最初に現れるステップの id。
 * 使う時期にその単元がまだ無い教材 (対象年齢が低く早めに始めるもの) を、見取り図のどのマスに
 * 出すかを決める。以降にも無ければ null。
 */
export function cellStageFor(model: AttainmentModel, u: Unit, stageId: string): string | null {
  const from = model.stages.findIndex((s) => s.id === stageId);
  if (from < 0) return null;
  for (const s of model.stages.slice(from)) {
    if (u.expected[s.id] !== undefined) return s.id;
  }
  return null;
}

/** 確認の印に付ける番号 (「確認（1 回目）」→ 1)。取れなければ ●。 */
export function checkMark(cp: CalibrationPoint): string {
  const m = /(\d+)/.exec(cp.label);
  return m ? m[1]! : "●";
}

/** 印の下に出す短い時期 (「年長の 10 月末」→「10 月末」。列が学年なので学年は省く)。 */
export function shortTiming(cp: CalibrationPoint): string {
  return cp.timing.replace(/^.*?の\s*/, "");
}
